// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the state container. Pure: no DOM, no network.
//
// A tiny observable store rather than a framework. The app has one screen stack
// and about twenty fields; the portal ships no state library, and adding one for
// this would be a dependency a phone downloads to hold a discriminated union.
//
// The UI subscribes, re-renders from a snapshot, and never mutates: every writer
// goes through `update()`, so there is one place a change can be traced to.
// ─────────────────────────────────────────────────────────────────────────────

import { type Appearance, DEFAULT_APPEARANCE } from "home/app/mobile/appearance";
import { type LanguageChoice } from "home/app/mobile/i18n";
import { type MobileProfile, type TodoCreateOptions } from "home/app/mobile/api";
import { type KnownTenant } from "home/app/mobile/tenants";
import { withKnownPermissions, withKnownStateIcons } from "home/app/mobile/todos";
import {
    type LifecycleTransition,
    type OnboardingStep,
    type Priority,
    type TenantDiscovery,
    type Todo,
    type TodoGrouping,
    type ListScope,
} from "home/app/mobile/types";

/** Which bottom-tab is showing. Two tabs — Home and TODOs. */
export type AppTab = "home" | "todos";

/** The TODO tab's own two views. */
export type TodoView = "list" | "detail";

/** Which bottom sheet is up, if any. */
// "account" is the only sheet not about a TODO — it is the app's one route to switching
// installation and to signing out, both of which have no other control anywhere.
export type SheetKind =
    "transition" | "priority" | "assignees" | "owner" | "new" | "account" | "details" | "delete" | null;

/**
 * The status sheet's draft: the move chosen and confirmed, while its request runs.
 *
 * ⚠ `transition` is NULL while the sheet is open and nothing is confirmed yet — the sheet opens with
 * NO target preselected, and picking one is local to the sheet until Confirm. It is set by
 * `confirmTransition()`, which is also what lets a refused move reopen with the person's pick.
 */
export interface TransitionDraft {
    transition: LifecycleTransition | null;
    label: string;
    note: string;
    /** True while the request is in flight — the confirm button locks. */
    busy: boolean;
    error: string;
}

export interface NewTodoDraft {
    title: string;
    projectId: string;
    assigneeId: string;
    /**
     * ⚠ A DATE, `YYYY-MM-DD`, not one of the old `Today`/`This week`/`Later` labels.
     *
     * The composer used to send a label and let the server decide what it meant, which put "this
     * week" on the server's calendar rather than the reader's and could not express any other day
     * at all. `""` is a real answer: a TODO nobody has dated is not the same as one due far away.
     */
    dueISO: string;
    /** A `TODO_PRIORITIES` id, or `""` for none — like the due date, nothing is set until somebody says so. */
    priority: Priority | "";
}

export interface MobileState {
    // — session —
    ready: boolean;
    signedIn: boolean;
    profile: MobileProfile | null;

    // — onboarding —
    onboarding: OnboardingStep;
    tenantInput: string;
    tenantError: string;
    discovery: TenantDiscovery | null;
    enrollmentId: string | null;
    authorizationUrl: string | null;
    authorizationDeadline: string | null;
    tokenStep: number;
    tokenFailedAt: number | null;
    onboardingError: string;
    /**
     * Whether window.open() actually produced a window — null until a pairing is started.
     *
     * ⚠ A blocked popup returns null and does NOT throw, so this is the only way to know. Safari on
     * iOS blocks it because the call happens in a promise callback, outside the user gesture.
     */
    approvalOpened: boolean | null;
    /**
     * The HTTP status the message came from, or null when it did not come from a response.
     * Carried so the failure screen can TITLE itself truthfully — a 403 means the installation
     * answered, which is the opposite of what a network failure looks like.
     */
    onboardingErrorStatus: number | null;

    /** The installations this device has paired with before. May be several. */
    knownTenants: KnownTenant[];
    /**
     * A session that existed and has ended — not the same as never having one.
     * It gets the "Welcome back" card and a one-tap re-auth, rather than sending
     * a returning user back through the address field.
     */
    sessionExpired: boolean;
    /** True while the address form is showing over a non-empty installation list. */
    addingTenant: boolean;
    /**
     * True while a tenant is being ADDED from inside the app (account sheet → Add another tenant):
     * the session on the current tenant is still valid, so the address step offers a way back to
     * it. False for every other road into onboarding — a first run, a sign-out, a lost session.
     */
    pairingFromApp: boolean;
    /** The method the user last chose, so a re-auth can offer it back by name. */
    lastMethodId: string | null;

    // — app —
    tab: AppTab;
    todoView: TodoView;
    /** The list's segment — see `ListScope`. */
    relation: ListScope;
    grouping: TodoGrouping;
    /** `TodoGroup.key`s the user has folded away in the list. Remembered on the device. */
    collapsedGroups: string[];
    /** Light, dark or the phone's own setting. Remembered on the device; see appearance.ts. */
    appearance: Appearance;
    /** The language picked in the account sheet, or `auto` for the PQFORCE account's. Device-wide. */
    languageChoice: LanguageChoice;
    selectedId: string | null;

    todos: Todo[];
    loading: boolean;
    loadError: string;
    /**
     * The "All TODOs" list, or null until it has been asked for — it is read only when that
     * segment is chosen, being every TODO the tenant lets this person see.
     */
    allTodos: Todo[] | null;
    allLoading: boolean;
    allError: string;

    sheet: SheetKind;
    /** The project the New TODO sheet starts with — set by a project group's + in the list. */
    newTodoProjectId: string | null;
    /**
     * Whether the search card is open over the screen. What it searches follows from the screen it
     * was opened on — see `searchContextOf()`; what is typed is NOT here (see `searchQuery` in
     * ui_mobile_todos.ts), because a keystroke through the store would rebuild the field under the
     * caret.
     */
    searchOpen: boolean;
    /**
     * What the app is busy doing, as a sentence, or null.
     *
     * ⚠ Only for work that REPLACES what is on screen — switching installation, signing out. The
     * ordinary `loading` flag is different: it paints an empty region while a list arrives, which
     * is right when the region is going to hold that list. During a switch the screen still holds
     * the OTHER installation's TODOs, and leaving them up while the new ones load is how somebody
     * comes to act on a row that belongs to a system they just left.
     */
    busyLabel: string | null;
    /** The TODO being read whole right now (`GET todos/{id}`) — its detail says "Loading…". */
    todoLoading: string | null;
    transitionDraft: TransitionDraft | null;
    commentDraft: string;
    commentBusy: boolean;
    /**
     * The resource id whose add or remove is in flight, or null. One at a time: each change is its
     * own request and answers with the whole TODO, so a second tap before the first returns would
     * repaint over it.
     */
    assigneeBusy: string | null;
    /** A priority change is in flight. One at a time, for the assignee reason: each answers with the whole TODO. */
    priorityBusy: boolean;
    /**
     * What the priority sheet has picked, not yet confirmed: an id, `""` for none, or null for nothing
     * picked. In the store so a refused change reopens with the pick; reset each time the sheet opens,
     * so nothing is ever preselected.
     */
    priorityPick: Priority | "" | null;
    /** The person picked in the hand-over sheet, before Confirm. Reset when the sheet opens. */
    ownerPick: string | null;
    newTodo: NewTodoDraft;
    createOptions: TodoCreateOptions | null;

    toast: string | null;
}

export const EMPTY_NEW_TODO: NewTodoDraft = {
    title: "",
    projectId: "",
    assigneeId: "",
    /** `YYYY-MM-DD`, or `""` for no due date. Absence is a legitimate value, not a missing answer. */
    dueISO: "",
    priority: "",
};

export function initialState(): MobileState {
    return {
        ready: false,
        signedIn: false,
        profile: null,

        onboarding: "url",
        tenantInput: "",
        tenantError: "",
        discovery: null,
        enrollmentId: null,
        authorizationUrl: null,
        authorizationDeadline: null,
        tokenStep: 0,
        tokenFailedAt: null,
        onboardingError: "",
        approvalOpened: null,
        onboardingErrorStatus: null,

        knownTenants: [],
        sessionExpired: false,
        addingTenant: false,
        lastMethodId: null,

        tab: "home",
        todoView: "list",
        relation: "assignee",
        allTodos: null,
        allLoading: false,
        allError: "",
        grouping: "due",
        collapsedGroups: [],
        appearance: DEFAULT_APPEARANCE,
        languageChoice: "auto",
        selectedId: null,

        todos: [],
        loading: false,
        loadError: "",

        sheet: null,
        newTodoProjectId: null,
        searchOpen: false,
        pairingFromApp: false,
        busyLabel: null,
        todoLoading: null,
        transitionDraft: null,
        commentDraft: "",
        commentBusy: false,
        assigneeBusy: null,
        priorityBusy: false,
        priorityPick: null,
        ownerPick: null,
        newTodo: { ...EMPTY_NEW_TODO },
        createOptions: null,

        toast: null,
    };
}

type Listener = (state: MobileState) => void;

export class MobileStore {
    private state: MobileState = initialState();
    private listeners: Listener[] = [];

    getState(): MobileState {
        return this.state;
    }

    /**
     * Shallow-merge a patch and notify. The only writer.
     *
     * Every write of `todos` passes through `withKnownStateIcons()` — here rather than at the half
     * dozen call sites, so the list load, the re-read after a change and a created todo cannot differ
     * about which icon a state wears. The rows being replaced are a source too.
     */
    update(patch: Partial<MobileState>): MobileState {
        // …and through `withKnownPermissions()`: a list never carries what this person may do, and a
        // reload must not take back what an opened TODO already learned.
        const known = [...this.state.todos, ...(this.state.allTodos ?? [])];
        if (patch.todos) {
            patch = {
                ...patch,
                todos: withKnownPermissions(withKnownStateIcons(patch.todos, this.state.todos), known),
            };
        }
        if (patch.allTodos) {
            patch = { ...patch, allTodos: withKnownPermissions(withKnownStateIcons(patch.allTodos, known), known) };
        }
        this.state = { ...this.state, ...patch };
        for (const listener of this.listeners) listener(this.state);
        return this.state;
    }

    subscribe(listener: Listener): () => void {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    /** The currently selected todo, or null — this person's own copy first, the "All" one else. */
    selected(): Todo | null {
        const { todos, allTodos, selectedId } = this.state;
        return todos.find((t) => t.id === selectedId) ?? allTodos?.find((t) => t.id === selectedId) ?? null;
    }

    /** Replace one todo in place, keeping list order. */
    /**
     * Replace one todo in place, keeping list order — in BOTH lists, since a TODO met in "All" may
     * also be one this person holds.
     */
    replaceTodo(todo: Todo): MobileState {
        const swap = (list: Todo[]) => list.map((t) => (t.id === todo.id ? todo : t));
        return this.update({
            todos: swap(this.state.todos),
            ...(this.state.allTodos ? { allTodos: swap(this.state.allTodos) } : {}),
        });
    }

    /** Take one todo out of BOTH lists — after it was deleted. */
    removeTodo(id: string): MobileState {
        const drop = (list: Todo[]) => list.filter((t) => t.id !== id);
        return this.update({
            todos: drop(this.state.todos),
            ...(this.state.allTodos ? { allTodos: drop(this.state.allTodos) } : {}),
        });
    }
}

/**
 * How long a toast stays up. The design's own figure.
 *
 * Exported so the UI's timer and any test read the same number rather than each
 * carrying a copy — the drift `DEFAULT_WINDOW_DAYS` exists to prevent on the
 * posts side.
 */
export const TOAST_MS = 2600;

/** How often the pairing poll asks whether the user has approved the token yet. */
export const PAIRING_POLL_MS = 2000;

/**
 * How long to keep polling before giving up.
 *
 * The tenant's own `authorizationDeadline` is the real bound and is preferred
 * whenever it parses; this is the fallback for a tenant that does not send one,
 * so the poll cannot run forever on a phone in someone's pocket.
 */
export const PAIRING_TIMEOUT_MS = 5 * 60 * 1000;
